import * as React from 'react'

import {
  ItAssetModelsStockDocument,
  JobCatalogNodeAttributesDevicesDocument,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'

import { Device, WizardData } from 'modules/employees/components/UnifiedAddEmployeeModal/types'
import { mapEquipmentData } from 'modules/job_catalog/lib/equipmentUtils'

type ItAsset = {
  id: string
  serialNumber: string
  itAssetModel?: {
    id: string
    brand: string
    name: string
    typeName: string
  } | null
}

interface UseDevicesManagerParams {
  data: WizardData
  onDataChanged: (updated: WizardData) => void
  itAssetsInStock: ItAsset[]
  onResetComplete?: () => void
}

export const useDevicesManager = ({
  data,
  onDataChanged,
  itAssetsInStock,
  onResetComplete,
}: UseDevicesManagerParams) => {
  const [devices, setDevices] = React.useState<Device[]>([])
  const [stockData, setStockData] = React.useState<Record<string, boolean>>({})
  const [showAddDeviceForm, setShowAddDeviceForm] = React.useState(false)
  const [selectedAssetId, setSelectedAssetId] = React.useState('')
  const [editingDevice, setEditingDevice] = React.useState<Device | null>(null)
  const [isInitialized, setIsInitialized] = React.useState(false)

  const roleLevelName = data.roleLevelName
  const nodeId = data.jobCatalogTreeNodeUuid
  const currentContext = React.useMemo(
    () => ({ nodeId: nodeId ?? null, roleLevelName: roleLevelName ?? null }),
    [nodeId, roleLevelName]
  )

  const { data: equipmentData, loading: equipmentLoading } = useQuery(
    JobCatalogNodeAttributesDevicesDocument,
    {
      variables: {
        nodeUuid: nodeId || '',
      },
      skip: !nodeId,
    }
  )

  const jobCatalogAttributes = equipmentData?.jobCatalog?.nodeAttributesConnection?.nodes
  const defaultEquipmentRecords = React.useMemo(() => {
    if (!jobCatalogAttributes || !nodeId) return []
    return mapEquipmentData(jobCatalogAttributes, nodeId)
  }, [jobCatalogAttributes, nodeId])

  const itAssetModelIds = React.useMemo(
    () => defaultEquipmentRecords.map((d) => d.id),
    [defaultEquipmentRecords]
  )
  const { data: stockCheckData } = useQuery(ItAssetModelsStockDocument, {
    variables: { itAssetModelIds },
    skip: itAssetModelIds.length === 0,
    fetchPolicy: 'no-cache',
  })

  React.useEffect(() => {
    if (!stockCheckData) return

    const nodes = stockCheckData?.itManagement?.itAssetModelsStocksConnection?.nodes
    if (Array.isArray(nodes)) {
      const stockMap: Record<string, boolean> = {}
      nodes.forEach((node: { id: string; hasStock: boolean }) => {
        stockMap[node.id] = node.hasStock
      })
      setStockData(stockMap)
    }
  }, [stockCheckData])

  const buildDefaultDevicesFromJobCatalog = React.useCallback((): Device[] => {
    if (defaultEquipmentRecords.length === 0) return []
    const stockNodes = stockCheckData?.itManagement?.itAssetModelsStocksConnection?.nodes || []
    return defaultEquipmentRecords.map((equipment) => {
      const stockNode = stockNodes.find((n: { id: string }) => n.id === equipment.id)
      return {
        id: crypto.randomUUID(),
        itAssetModelId: equipment.id,
        name: equipment.name,
        brand: equipment.brand,
        type: equipment.typeName,
        itAssetId: stockNode && stockNode.itAssetId ? stockNode.itAssetId : undefined,
        serialNumber: stockNode && stockNode.serialNumber ? stockNode.serialNumber : undefined,
        hasStock: stockNode ? stockNode.hasStock : false,
      }
    })
  }, [defaultEquipmentRecords, stockCheckData])

  React.useEffect(() => {
    if (isInitialized) return

    const previousContext = data.selectedDevicesContext
    const hasRoleLevelChanged =
      (previousContext?.nodeId ?? null) !== currentContext.nodeId ||
      (previousContext?.roleLevelName ?? null) !== currentContext.roleLevelName

    if (!hasRoleLevelChanged && data.selectedDevices && data.selectedDevices.length > 0) {
      setDevices(data.selectedDevices)
      setIsInitialized(true)
      return
    }

    const shouldWaitForDefaultDevices = nodeId && equipmentLoading
    if (shouldWaitForDefaultDevices) return

    const shouldWaitForStock = itAssetModelIds.length > 0 && !stockCheckData
    if (shouldWaitForStock) return

    const defaults = buildDefaultDevicesFromJobCatalog()

    if (hasRoleLevelChanged) {
      setDevices(defaults)
      setIsInitialized(true)
      return
    }

    const firstVisit = previousContext == null
    if (firstVisit) {
      setDevices(defaults)
      setIsInitialized(true)

      return
    }

    setDevices(defaults)
    setIsInitialized(true)
  }, [
    data.selectedDevices,
    data.selectedDevicesContext,
    currentContext,
    isInitialized,
    jobCatalogAttributes,
    nodeId,
    equipmentLoading,
    stockCheckData,
    itAssetModelIds,
    buildDefaultDevicesFromJobCatalog,
  ])

  // Update wizard state
  React.useEffect(() => {
    const validDevices = devices.every((device) => {
      if (device.hasStock) return true
      return stockData[device.itAssetModelId] ?? false
    })

    const currentSelectedDevices = data.selectedDevices || []
    const currentSelectedDevicesContext = data.selectedDevicesContext
    const currentValidDevices = data.validDevices

    const devicesChanged = JSON.stringify(currentSelectedDevices) !== JSON.stringify(devices)
    const contextChanged =
      JSON.stringify(currentSelectedDevicesContext) !== JSON.stringify(currentContext)
    const validDevicesChanged = currentValidDevices !== validDevices

    if (!devicesChanged && !contextChanged && !validDevicesChanged) {
      return
    }

    onDataChanged({
      ...data,
      selectedDevices: devices,
      selectedDevicesContext: currentContext,
      validDevices,
    })
  }, [devices, stockData, data, onDataChanged, currentContext])

  const handleSaveDevice = () => {
    if (!selectedAssetId) return

    const selectedAsset = itAssetsInStock.find((asset) => asset.id === selectedAssetId)
    if (!selectedAsset) return

    const itAssetModel = selectedAsset.itAssetModel
    if (!itAssetModel) return

    const newDevice: Device = {
      id: editingDevice?.id ?? crypto.randomUUID(),
      itAssetModelId: itAssetModel.id,
      name: itAssetModel.name,
      brand: itAssetModel.brand,
      type: itAssetModel.typeName,
      serialNumber: selectedAsset.serialNumber,
      itAssetId: selectedAsset.id,
      hasStock: true,
    }

    if (editingDevice) {
      setDevices((prev) =>
        prev.map((device) => (device.id === editingDevice.id ? newDevice : device))
      )
      setEditingDevice(null)
    } else {
      setDevices((prev) => [...prev, newDevice])
    }

    setSelectedAssetId('')
    setShowAddDeviceForm(false)
  }

  const handleEditDevice = (device: Device) => {
    setEditingDevice(device)
    setSelectedAssetId(device.itAssetId ?? '')
    setShowAddDeviceForm(false)
  }

  const handleCancelEdit = () => {
    setEditingDevice(null)
    setSelectedAssetId('')
    setShowAddDeviceForm(false)
  }

  const handleDeleteDevice = (deviceId: string) => {
    setDevices((prev) => prev.filter((d) => d.id !== deviceId))
  }

  const handleResetToDefault = () => {
    if (!nodeId) return
    const defaults = buildDefaultDevicesFromJobCatalog()
    setDevices(defaults)
    setShowAddDeviceForm(false)
    onResetComplete?.()
  }

  const usedAssetIds = React.useMemo(() => {
    return new Set(devices.map((device) => device.itAssetId).filter(Boolean))
  }, [devices])

  return {
    // state
    devices,
    showAddDeviceForm,
    selectedAssetId,
    editingDevice,
    equipmentLoading,
    hasRecommendedDevices: defaultEquipmentRecords.length > 0,
    usedAssetIds,
    // setters
    setShowAddDeviceForm,
    setSelectedAssetId,
    // actions
    handleSaveDevice,
    handleEditDevice,
    handleCancelEdit,
    handleDeleteDevice,
    handleResetToDefault,
    // context
    nodeId,
    roleLevelName,
  }
}
